import { useEffect, useState, useMemo } from 'react';
import { createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Route as EncodersLayout } from './_layout';
import { Layout } from '@/client/layouts/layout';
import { Button } from '@/client/components/ui/button';
import { Badge } from '@/client/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import {
  RefreshCw,
  Stethoscope,
  Wifi,
  WifiOff,
  Database,
  HardDrive,
  AlertTriangle,
  Radio,
  VolumeX,
  FileText,
  X,
  Copy,
  Check,
} from 'lucide-react';
import { API_BASE_URL } from '@/client/config';
import { ExportModal } from '@/client/components/export-modal';
import { useAlertStore, getAlertKey, type TriageSessionWithSample } from '@/client/store/alertStore';
import { useEncoderWSSStore } from '@/client/store/encoderWSSStore';
import { useShallow } from 'zustand/react/shallow';
import { AlertCard } from '@/client/components/alert-card';
import { GroupedAlertCard } from '@/client/components/grouped-alert-card';
import { EncoderMonitorCard } from '@/client/components/encoder/EncoderMonitorCard';
import type { PollerStats } from '@shared/types';

export const Route = createRoute({
  getParentRoute: () => EncodersLayout,
  path: 'encoders/triage',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: EncoderTriageComponent,
});

function formatDuration(startedAt: number): string {
  const diff = Date.now() - startedAt;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

function formatBitrate(bitrate?: number): string {
  if (!bitrate) return '-';
  if (bitrate >= 1_000_000) return `${(bitrate / 1_000_000).toFixed(2)} Mbps`;
  if (bitrate >= 1_000) return `${(bitrate / 1_000).toFixed(0)} Kbps`;
  return `${bitrate} bps`;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(2)} MB`;
  if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(1)} KB`;
  return `${bytes} B`;
}

function EncoderTriageComponent() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollerStats, setPollerStats] = useState<PollerStats | null>(null);
  const [pinningEncoder, setPinningEncoder] = useState<string | null>(null);
  const [confirmingUnpin, setConfirmingUnpin] = useState<string | null>(null);
  const [copiedEncoderId, setCopiedEncoderId] = useState<string | null>(null);

  // Severity filter state
  const [severityFilter, setSeverityFilter] = useState<'all' | 'critical' | 'warning' | 'info'>('all');

  // Alert type filter state - set of hidden alert types
  const [hiddenAlertTypes, setHiddenAlertTypes] = useState<Set<string>>(new Set());

  // Export modal state
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportEncoderId, setExportEncoderId] = useState<string | null>(null);

  // Copy encoder ID to clipboard
  const handleCopyEncoderId = async (encoderId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(encoderId);
      setCopiedEncoderId(encoderId);
      setTimeout(() => setCopiedEncoderId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Get sessions and alerts from the store (auto-updated via SSE in layout.tsx)
  const sessions = useAlertStore((s) => s.triageSessions);
  const sseStatus = useAlertStore((s) => s.sseStatus);
  const alertsMap = useAlertStore((s) => s.alerts);
  const mutedAlerts = useAlertStore((s) => s.mutedAlerts);
  const addTriageSession = useAlertStore((s) => s.addTriageSession);
  const setTriageSessions = useAlertStore((s) => s.setTriageSessions);
  const removeTriageSession = useAlertStore((s) => s.removeTriageSession);
  const muteAlert = useAlertStore((s) => s.muteAlert);
  const unmuteAlert = useAlertStore((s) => s.unmuteAlert);

  // Get encoder WSS monitoring data - convert Map to array in selector for stable reference
  const encodersWithIssues = useEncoderWSSStore(
    useShallow((s) => {
      return Array.from(s.encoders.values())
        .filter((e) => e.state !== 'normal')
        .sort((a, b) => {
          if (a.state === 'alert' && b.state !== 'alert') return -1;
          if (b.state === 'alert' && a.state !== 'alert') return 1;
          // Stable alpha sort by name - prevents jumping when alerts update
          return a.simpleName.localeCompare(b.simpleName);
        });
    })
  );

  const isConnected = sseStatus === 'connected';

  const { alerts, mutedAlertsList, alertTypeCounts, groupedAlerts, singleAlerts } = useMemo(() => {
    const active: Array<{ encoderId: string; alertType: string; alert: ReturnType<typeof alertsMap.get> extends Map<string, infer V> | undefined ? V : never }> = [];
    const muted: Array<{ encoderId: string; alertType: string; alert: ReturnType<typeof alertsMap.get> extends Map<string, infer V> | undefined ? V : never }> = [];
    const typeCounts: Record<string, number> = {};

    for (const [encoderId, encoderAlerts] of alertsMap.entries()) {
      for (const [alertType, alert] of encoderAlerts.entries()) {
        const key = getAlertKey(encoderId, alertType);
        // Count all alert types (before muting filter)
        typeCounts[alertType] = (typeCounts[alertType] || 0) + 1;

        if (mutedAlerts.has(key)) {
          muted.push({ encoderId, alertType, alert });
        } else {
          active.push({ encoderId, alertType, alert });
        }
      }
    }

    // Sort by severity (critical first) then by encoderId
    const severityOrder: Record<string, number> = { critical: 0, warning: 1, info: 2 };
    const sortFn = (a: typeof active[0], b: typeof active[0]) => {
      const aSev = severityOrder[a.alert.severity] ?? 3;
      const bSev = severityOrder[b.alert.severity] ?? 3;
      if (aSev !== bSev) return aSev - bSev;
      return a.encoderId.localeCompare(b.encoderId);
    };

    const sortedActive = active.sort(sortFn);

    // Group alerts by encoderId - only group if encoder has >1 alert
    const tempGrouped = new Map<string, typeof sortedActive>();
    for (const item of sortedActive) {
      const existing = tempGrouped.get(item.encoderId) || [];
      existing.push(item);
      tempGrouped.set(item.encoderId, existing);
    }

    // Separate into grouped (>1 alert) and singles (exactly 1 alert)
    const multiAlertGroups: Array<[string, typeof sortedActive]> = [];
    const singleAlerts: typeof sortedActive = [];

    for (const [encoderId, encoderAlerts] of tempGrouped.entries()) {
      if (encoderAlerts.length > 1) {
        multiAlertGroups.push([encoderId, encoderAlerts]);
      } else {
        singleAlerts.push(...encoderAlerts);
      }
    }

    // Sort grouped entries by highest severity alert in each group
    multiAlertGroups.sort(([, aAlerts], [, bAlerts]) => {
      const aMaxSev = Math.min(...aAlerts.map(a => severityOrder[a.alert.severity] ?? 3));
      const bMaxSev = Math.min(...bAlerts.map(b => severityOrder[b.alert.severity] ?? 3));
      return aMaxSev - bMaxSev;
    });

    // Sort single alerts by severity
    singleAlerts.sort(sortFn);

    return {
      alerts: sortedActive.map(({ alert }) => alert),
      mutedAlertsList: muted.sort(sortFn).map(({ alert }) => alert),
      alertTypeCounts: typeCounts,
      groupedAlerts: multiAlertGroups,
      singleAlerts: singleAlerts.map(({ alert }) => alert),
    };
  }, [alertsMap, mutedAlerts]);

  // Filter alerts by severity and hidden types
  const filteredAlerts = useMemo(() => {
    let filtered = alerts;
    if (severityFilter !== 'all') {
      filtered = filtered.filter((alert) => alert.severity === severityFilter);
    }
    if (hiddenAlertTypes.size > 0) {
      filtered = filtered.filter((alert) => !hiddenAlertTypes.has(alert.alertType));
    }
    return filtered;
  }, [alerts, severityFilter, hiddenAlertTypes]);

  // Filter grouped alerts and single alerts by severity and hidden types
  const { filteredGroupedAlerts, filteredSingleAlerts } = useMemo(() => {
    const filterFn = (alert: typeof alerts[0]) => {
      if (severityFilter !== 'all' && alert.severity !== severityFilter) return false;
      if (hiddenAlertTypes.size > 0 && hiddenAlertTypes.has(alert.alertType)) return false;
      return true;
    };

    // Filter grouped alerts - keep groups that have at least one visible alert
    const filteredGroups = groupedAlerts
      .map(([encoderId, encoderAlerts]) => {
        const visibleAlerts = encoderAlerts.filter(item => filterFn(item.alert));
        return [encoderId, visibleAlerts] as [string, typeof encoderAlerts];
      })
      .filter(([, visibleAlerts]) => visibleAlerts.length > 0);

    // Filter single alerts
    const filteredSingles = singleAlerts.filter(filterFn);

    return {
      filteredGroupedAlerts: filteredGroups,
      filteredSingleAlerts: filteredSingles,
    };
  }, [groupedAlerts, singleAlerts, severityFilter, hiddenAlertTypes]);

  // Count alerts by severity for filter badges
  const severityCounts = useMemo(() => ({
    critical: alerts.filter((a) => a.severity === 'critical').length,
    warning: alerts.filter((a) => a.severity === 'warning').length,
    info: alerts.filter((a) => a.severity === 'info').length,
  }), [alerts]);

  // Check if encoder is already in triage
  const triageEncoderIds = useMemo(
    () => new Set(sessions.map((s) => s.encoderId)),
    [sessions]
  );

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/encoders/triage`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch triage sessions');

      const data = await response.json();
      setTriageSessions(data.sessions || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPollerStats = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/encoders/poller/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setPollerStats(data.poller);
      }
    } catch (err) {
      console.error('Failed to fetch poller stats:', err);
    }
  };

  const handleStopTriage = async (encoderId: string) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE_URL}/api/encoders/${encoderId}/triage`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      // Store will be updated via SSE broadcast, but update immediately for responsiveness
      removeTriageSession(encoderId);
    } catch (err) {
      console.error('Failed to stop triage:', err);
    }
  };

  const handlePinToTriage = async (encoderId: string) => {
    try {
      setPinningEncoder(encoderId);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/encoders/${encoderId}/triage`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes: 'Pinned from Active Alerts' }),
      });

      if (!response.ok) throw new Error('Failed to start triage');

      const data = await response.json();
      addTriageSession(data.session);
    } catch (err) {
      console.error('Failed to pin encoder to triage:', err);
    } finally {
      setPinningEncoder(null);
    }
  };

  const handleExport = (encoderId: string) => {
    setExportEncoderId(encoderId);
    setExportModalOpen(true);
  };

  useEffect(() => {
    fetchSessions();
    fetchPollerStats();

    // Refresh poller stats every 30 seconds
    const interval = setInterval(fetchPollerStats, 30000);
    return () => clearInterval(interval);
  }, [getAccessTokenSilently]);

  return (
    <Layout>
      <div>
        <section>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Stethoscope className="h-6 w-6" />
              <h3 className="text-2xl font-semibold">Encoder Triage</h3>
              {isConnected ? (
                <Badge variant="outline" className="bg-green-50 text-green-700 gap-1">
                  <Wifi className="h-3 w-3" />
                  Polling
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-gray-50 text-gray-500 gap-1">
                  <WifiOff className="h-3 w-3" />
                  {sseStatus === 'connecting' ? 'Connecting...' : 'Disconnected'}
                </Badge>
              )}
            </div>
            <Button variant="outline" onClick={fetchSessions} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Ring Buffer Stats */}
          {pollerStats?.ringBufferStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="rounded-lg border p-4">
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                  <Database className="h-4 w-4" />
                  Total Encoders
                </div>
                <div className="text-2xl font-semibold">
                  {pollerStats.ringBufferStats.totalEncoders}
                </div>
              </div>
              <div className="rounded-lg border p-4">
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                  <Stethoscope className="h-4 w-4" />
                  In Triage
                </div>
                <div className="text-2xl font-semibold">
                  {pollerStats.ringBufferStats.triageEncoders}
                </div>
              </div>
              <div className="rounded-lg border p-4">
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                  <Database className="h-4 w-4" />
                  Total Samples
                </div>
                <div className="text-2xl font-semibold">
                  {pollerStats.ringBufferStats.totalSamples.toLocaleString()}
                </div>
              </div>
              <div className="rounded-lg border p-4">
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                  <HardDrive className="h-4 w-4" />
                  Est. Memory
                </div>
                <div className="text-2xl font-semibold">
                  {formatBytes(pollerStats.ringBufferStats.estimatedMemoryBytes)}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded mb-4">
              <p className="m-0 text-red-800 font-medium">Error: {error}</p>
            </div>
          )}

          {/* Connection Monitoring Section (WSS-based flapping detection) */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Radio className="h-5 w-5 text-blue-600" />
              <h4 className="text-lg font-semibold">Connection Monitoring</h4>
              {encodersWithIssues.length > 0 && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  {encodersWithIssues.length}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground ml-2">Real-time connection status</span>
            </div>

            {encodersWithIssues.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground border rounded-lg border-dashed">
                <Radio className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No encoder connection issues detected</p>
                <p className="text-xs mt-1">Rapid connect/disconnect cycles will appear here</p>
              </div>
            ) : (
              <div className="space-y-2">
                {encodersWithIssues.map((encoder) => (
                  <EncoderMonitorCard
                    key={encoder.encoderID}
                    encoder={encoder}
                    onExport={handleExport}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Active Alerts Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <h4 className="text-lg font-semibold">Active Alerts</h4>
                {alerts.length > 0 && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                    {alerts.length}
                  </Badge>
                )}
                {mutedAlertsList.length > 0 && (
                  <span className="text-xs text-muted-foreground ml-2">
                    ({mutedAlertsList.length} muted)
                  </span>
                )}
              </div>

              {/* Severity Filter Tabs */}
              {alerts.length > 0 && (
                <div className="flex gap-1">
                  <Button
                    variant={severityFilter === 'all' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setSeverityFilter('all')}
                    className="h-7 text-xs"
                  >
                    All
                  </Button>
                  <Button
                    variant={severityFilter === 'critical' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setSeverityFilter('critical')}
                    className="h-7 text-xs gap-1"
                    disabled={severityCounts.critical === 0}
                  >
                    <span className="h-2 w-2 rounded-full bg-red-500" />
                    Critical
                    {severityCounts.critical > 0 && (
                      <span className="text-muted-foreground">({severityCounts.critical})</span>
                    )}
                  </Button>
                  <Button
                    variant={severityFilter === 'warning' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setSeverityFilter('warning')}
                    className="h-7 text-xs gap-1"
                    disabled={severityCounts.warning === 0}
                  >
                    <span className="h-2 w-2 rounded-full bg-amber-500" />
                    Warning
                    {severityCounts.warning > 0 && (
                      <span className="text-muted-foreground">({severityCounts.warning})</span>
                    )}
                  </Button>
                  <Button
                    variant={severityFilter === 'info' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setSeverityFilter('info')}
                    className="h-7 text-xs gap-1"
                    disabled={severityCounts.info === 0}
                  >
                    <span className="h-2 w-2 rounded-full bg-blue-500" />
                    Info
                    {severityCounts.info > 0 && (
                      <span className="text-muted-foreground">({severityCounts.info})</span>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Alert Type Filters - show if there are alerts */}
            {alerts.length > 0 && Object.keys(alertTypeCounts).length > 0 && (
              <div className="flex flex-wrap gap-1 mb-4">
                <span className="text-xs text-muted-foreground mr-2 self-center">Filter types:</span>
                {Object.entries(alertTypeCounts)
                  .sort(([, a], [, b]) => b - a)
                  .map(([alertType, count]) => {
                    const isHidden = hiddenAlertTypes.has(alertType);
                    const label = alertType
                      .split('_')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ');
                    return (
                      <Button
                        key={alertType}
                        variant={isHidden ? 'outline' : 'secondary'}
                        size="sm"
                        onClick={() => {
                          const newHidden = new Set(hiddenAlertTypes);
                          if (isHidden) {
                            newHidden.delete(alertType);
                          } else {
                            newHidden.add(alertType);
                          }
                          setHiddenAlertTypes(newHidden);
                        }}
                        className={`h-6 text-xs ${isHidden ? 'opacity-50 line-through' : ''}`}
                      >
                        {label} ({count})
                      </Button>
                    );
                  })}
                {hiddenAlertTypes.size > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setHiddenAlertTypes(new Set())}
                    className="h-6 text-xs text-muted-foreground"
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            )}

            {alerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border rounded-lg border-dashed">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No active anomalies detected</p>
                <p className="text-xs mt-1">Encoders with issues will appear here automatically</p>
              </div>
            ) : filteredAlerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border rounded-lg border-dashed">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No alerts match current filters</p>
                <p className="text-xs mt-1">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                      setSeverityFilter('all');
                      setHiddenAlertTypes(new Set());
                    }}
                    className="h-auto p-0 text-xs"
                  >
                    Clear all filters
                  </Button>
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Grouped alerts (encoders with multiple alerts) */}
                {filteredGroupedAlerts.map(([encoderId, encoderAlerts]) => (
                  <GroupedAlertCard
                    key={`group-${encoderId}`}
                    encoderId={encoderId}
                    alerts={encoderAlerts.map(a => a.alert)}
                    isInTriage={triageEncoderIds.has(encoderId)}
                    onPinToTriage={handlePinToTriage}
                    isPinning={pinningEncoder === encoderId}
                    onMute={muteAlert}
                    onUnmute={unmuteAlert}
                    onExport={handleExport}
                  />
                ))}
                {/* Single alerts (encoders with only one alert) */}
                {filteredSingleAlerts.map((alert) => (
                  <AlertCard
                    key={`${alert.encoderId}-${alert.alertType}`}
                    alert={alert}
                    isInTriage={triageEncoderIds.has(alert.encoderId)}
                    onPinToTriage={handlePinToTriage}
                    isPinning={pinningEncoder === alert.encoderId}
                    isMuted={false}
                    onMute={muteAlert}
                    onUnmute={unmuteAlert}
                    onExport={handleExport}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Active Triage Sessions Section */}
          <div className="flex items-center gap-2 mb-4">
            <Stethoscope className="h-5 w-5 text-red-600" />
            <h4 className="text-lg font-semibold">Active Triage Sessions</h4>
            {sessions.length > 0 && (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                {sessions.length}
              </Badge>
            )}
          </div>

          {loading && <p>Loading triage sessions...</p>}

          {!loading && sessions.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Stethoscope className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">No Active Triage Sessions</p>
              <p className="text-sm mt-2">
                Start a triage session from an encoder's detail page to monitor it here.
              </p>
            </div>
          )}

          {!loading && sessions.length > 0 && (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Encoder ID</TableHead>
                    <TableHead>Stream Name</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Bitrate</TableHead>
                    <TableHead>Resolution</TableHead>
                    <TableHead>Frame Rate</TableHead>
                    <TableHead>Started By</TableHead>
                    <TableHead className="w-40">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow
                      key={session.encoderId}
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => navigate({ to: '/encoders/$encoderID', params: { encoderID: session.encoderId } })}
                    >
                      <TableCell
                        className="font-mono text-sm cursor-pointer"
                        onClick={(e) => handleCopyEncoderId(session.encoderId, e)}
                        title="Click to copy"
                      >
                        <span className="flex items-center gap-1.5">
                          {session.encoderId}
                          {copiedEncoderId === session.encoderId ? (
                            <Check className="h-3 w-3 text-green-600" />
                          ) : (
                            <Copy className="h-3 w-3 text-muted-foreground" />
                          )}
                        </span>
                      </TableCell>
                      <TableCell>{session.streamName || '-'}</TableCell>
                      <TableCell>{formatDuration(session.startedAt)}</TableCell>
                      <TableCell className="font-mono">
                        {formatBitrate(session.latestSample?.bitrate)}
                      </TableCell>
                      <TableCell className="font-mono">
                        {session.latestSample?.width && session.latestSample?.height
                          ? `${session.latestSample.width}x${session.latestSample.height}`
                          : '-'}
                      </TableCell>
                      <TableCell className="font-mono">
                        {session.latestSample?.real_frame_rate || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground truncate max-w-[150px]">
                        {session.startedBy || '-'}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleExport(session.encoderId)}
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Export
                          </Button>
                          {confirmingUnpin === session.encoderId ? (
                            <div className="flex gap-1">
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  handleStopTriage(session.encoderId);
                                  setConfirmingUnpin(null);
                                }}
                                autoFocus
                              >
                                <AlertTriangle className="h-4 w-4 mr-1" />
                                Confirm
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setConfirmingUnpin(null)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setConfirmingUnpin(session.encoderId)}
                            >
                              Unpin
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <p className="mt-4 text-sm text-muted-foreground">
            {sessions.length} active triage session{sessions.length !== 1 ? 's' : ''}
          </p>

          {/* Muted Alerts Section */}
          {mutedAlertsList.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center gap-2 mb-4">
                <VolumeX className="h-5 w-5 text-muted-foreground" />
                <h4 className="text-lg font-semibold text-muted-foreground">Muted Alerts</h4>
                <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">
                  {mutedAlertsList.length}
                </Badge>
                <span className="text-xs text-muted-foreground">Muted only for you</span>
              </div>

              <div className="space-y-2 opacity-60">
                {mutedAlertsList.map((alert) => (
                  <AlertCard
                    key={`${alert.encoderId}-${alert.alertType}-muted`}
                    alert={alert}
                    isInTriage={triageEncoderIds.has(alert.encoderId)}
                    onPinToTriage={handlePinToTriage}
                    isPinning={pinningEncoder === alert.encoderId}
                    isMuted={true}
                    onMute={muteAlert}
                    onUnmute={unmuteAlert}
                    onExport={handleExport}
                  />
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Export Modal */}
      <ExportModal
        encoderId={exportEncoderId}
        open={exportModalOpen}
        onOpenChange={setExportModalOpen}
      />
    </Layout>
  );
}
